import type { ApiAnswerOption } from '../../../../internalTypes';
import type { ApiQuestionSetting } from '../../../../internalTypes/apiQuestionSetting';
import type {
  McqOtherContentType,
  McqOtherDateFormat,
  McqOtherOptionConfig,
} from '../../../../types/mcq';

function findConfig(configs: ApiQuestionSetting[] | undefined, key: string): ApiQuestionSetting | undefined {
  return configs?.find(c => c.key === key);
}

function firstValue(config: ApiQuestionSetting | undefined): string | undefined {
  return config?.value?.[0];
}

function parseNumber(raw: string | undefined): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function mapDateFormat(raw: string | undefined): McqOtherDateFormat {
  const normalized = raw?.toLowerCase() ?? '';
  if (normalized.includes('yyyy') && normalized.indexOf('yyyy') < normalized.indexOf('mm')) {
    return 'YYYY/MM/DD';
  }
  if (normalized.startsWith('mm')) {
    return 'MM/DD/YYYY';
  }
  return 'DD/MM/YYYY';
}

function mapContentType(raw: string | undefined): McqOtherContentType {
  const upper = raw?.toUpperCase() ?? 'TEXT';
  if (upper === 'PHONE_NUMBER' || upper === 'POSTAL_CODE' || upper === 'DATE' || upper === 'NUMERIC' || upper === 'EMAIL') {
    return upper;
  }
  return 'TEXT';
}

function extractContentTypeConfig(otherConfig: ApiQuestionSetting): McqOtherOptionConfig | undefined {
  const contentTypeConfig = findConfig(otherConfig.childConfigs, 'CONTENT_TYPE');
  const contentType = mapContentType(firstValue(contentTypeConfig));
  const contentChildren = contentTypeConfig?.childConfigs ?? [];
  const errorMessage = firstValue(findConfig(contentChildren, 'ERROR_MESSAGE'));

  const base = {
    label: '',
    helpText: '',
    markRequired: false,
    ...(errorMessage !== undefined ? { errorMessage } : {}),
  };

  switch (contentType) {
    case 'TEXT': {
      const minCharacters = parseNumber(firstValue(findConfig(contentChildren, 'MIN_CHARACTER_COUNT')));
      const maxCharacters = parseNumber(firstValue(findConfig(contentChildren, 'MAX_CHARACTER_COUNT')));
      return {
        ...base,
        contentType: 'TEXT',
        ...(minCharacters !== undefined ? { minCharacters } : {}),
        ...(maxCharacters !== undefined ? { maxCharacters } : {}),
      };
    }
    case 'NUMERIC': {
      const minNumber = parseNumber(firstValue(findConfig(contentChildren, 'MIN_NUMBER')));
      const maxNumber = parseNumber(firstValue(findConfig(contentChildren, 'MAX_NUMBER')));
      const decimalCount = parseNumber(firstValue(findConfig(contentChildren, 'DECIMAL_COUNT')));
      return {
        ...base,
        contentType: 'NUMERIC',
        ...(minNumber !== undefined ? { minNumber } : {}),
        ...(maxNumber !== undefined ? { maxNumber } : {}),
        ...(decimalCount !== undefined ? { decimalCount } : {}),
      };
    }
    case 'EMAIL':
      return { ...base, contentType: 'EMAIL' };
    case 'PHONE_NUMBER':
    case 'POSTAL_CODE': {
      const countryCodes = findConfig(contentChildren, 'COUNTRY_CODE')?.value ?? [];
      return {
        ...base,
        contentType,
        countryCodes,
      };
    }
    case 'DATE': {
      const dateFormat = mapDateFormat(firstValue(findConfig(contentChildren, 'DATE_FORMAT')));
      const dateRange = findConfig(contentChildren, 'DATE_RANGE')?.value ?? [];
      const minDate = parseNumber(dateRange[0]);
      const maxDate = parseNumber(dateRange[1]);
      return {
        ...base,
        contentType: 'DATE',
        dateFormat,
        ...(minDate !== undefined ? { minDate } : {}),
        ...(maxDate !== undefined ? { maxDate } : {}),
      };
    }
    default:
      return { ...base, contentType: 'TEXT' };
  }
}

export function extractOtherOptionConfig(
  answerDetail: ApiAnswerOption,
  fallbackLabel: string
): McqOtherOptionConfig | undefined {
  const otherConfig = answerDetail.answerDetailConfigs?.find(c => c.key === 'OTHER_OPTION');
  if (firstValue(otherConfig)?.toLowerCase() !== 'true') {
    return undefined;
  }

  const childConfigs = otherConfig?.childConfigs ?? [];
  const label = firstValue(findConfig(childConfigs, 'LABEL')) ?? fallbackLabel;
  const helpText = firstValue(findConfig(childConfigs, 'HELP_TEXT')) ?? '';
  const markRequired = firstValue(findConfig(childConfigs, 'MARK_REQUIRED'))?.toLowerCase() === 'true';
  const placeholder = answerDetail.inputPlaceholder;

  const contentConfig = extractContentTypeConfig(otherConfig!);
  if (!contentConfig) {
    return undefined;
  }

  return {
    ...contentConfig,
    label,
    helpText,
    markRequired,
    ...(placeholder ? { placeholder } : {}),
  };
}
